import { NgIf } from '@angular/common';
import { Component, Input, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { BusInListDto, BusesApi, TenantUsersDto } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-available-buses-widget',
  standalone: true,
  imports: [NgIf, NzBadgeModule],
  template: `
    <button
      (click)="navigateToAvailableBuses()"
      class="header-action-button available-buses-button"
    >
      <span class="fa fa-bus"></span>
      <span class="button-label">Available Buses</span>
      @if (isLoading()) {
        <div class="badge badge-ghost ml-2">
          <span class="loading loading-ring loading-sm"></span>
        </div>
      } @else {
        <nz-badge [nzCount]="availableBusesCount()" [nzOverflowCount]="99" [nzShowZero]="true" class="ml-2">
        </nz-badge>
      }
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .available-buses-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 8px;
        transition: all var(--transition-fast);
        white-space: nowrap;
        color: var(--header-action);

        .fa-bus {
          font-size: 16px;
        }

        .button-label {
          font-size: 14px;
          font-weight: 500;
        }

        &:hover {
          background-color: var(--header-action-hover);
          color: var(--header-text);
        }
      }

      .badge {
        min-width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
      }

      .badge-ghost {
        background-color: var(--sidebar-hover);
        border: 1px solid var(--sidebar-border);
      }

      @media (max-width: 1024px) {
        .available-buses-button .button-label {
          display: none;
        }
      }
    `,
  ],
})
export class AvailableBusesWidgetComponent {
  @Input() authUser: TenantUsersDto | null = null;

  private busesApi = inject(BusesApi);
  private router = inject(Router);

  // Query for available buses count
  availableBusesQuery = injectQuery(() => {
    const user = this.authUser;
    // For tenant admin, use placeholder 'all' (backend ignores it and returns all buses)
    // For terminal users, use their terminalId
    const terminalId = this.shouldShowTerminalBuses(user) && user?.terminalId ? user.terminalId : 'all';

    return {
      queryKey: ['available-buses-header', terminalId],
      queryFn: () => {
        // Backend handles 'all' for tenant admin and returns all buses
        // For terminal users, it uses the actual terminalId
        return lastValueFrom(
          this.busesApi.busControllerGetAvailableBuses({
            terminalId: terminalId,
          }),
        );
      },
      enabled: !!user,
      refetchInterval: 5000, // Poll every 5 seconds for real-time updates
      refetchOnWindowFocus: false,
      staleTime: 4000, // Consider data fresh for 4 seconds
      gcTime: 60000, // Keep in cache for 1 minute
      retry: 2,
      // Keep previous data visible during refetches to prevent flickering
      placeholderData: (previousData: BusInListDto[] | undefined) => previousData,
      select: (data: BusInListDto[]) => data.length, // Just return count
    };
  });

  // Computed signals
  availableBusesCount = computed(() => {
    return this.availableBusesQuery.data() ?? 0;
  });

  isLoading = computed(() => {
    return this.availableBusesQuery.isLoading() && !this.availableBusesQuery.data();
  });

  shouldShowTerminalBuses(user: TenantUsersDto | null): boolean {
    if (!user) return false;
    const terminalRoles = [
      TenantUserRoles.TENANT_TERMINAL_MANAGER,
      TenantUserRoles.TENANT_PARCEL_MANAGER,
      TenantUserRoles.TERMINAL_BOOKING_CLERK,
      TenantUserRoles.TERMINAL_PARCEL_CLERK,
    ];
    return terminalRoles.includes(user.role as TenantUserRoles);
  }

  getNavigationRoute(): string {
    if (!this.authUser) {
      return '/operations/available-buses';
    }
    return this.shouldShowTerminalBuses(this.authUser)
      ? '/operations/terminal-available-buses'
      : '/operations/available-buses';
  }

  navigateToAvailableBuses() {
    const route = this.getNavigationRoute();
    this.router.navigate([route]);
  }
}
